import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { PublicLayout } from '@/components/public/PublicLayout';
import { Seo } from '@/components/Seo';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { CourseEnrollDialog } from '@/components/public/CourseEnrollDialog';
import { coursesService } from '@/services/courses.service';
import { mediaUrl } from '@/lib/api';
import {
  GraduationCap, Monitor, MapPin, CalendarDays, Clock, ArrowLeft, Globe, BarChart3, Users, Video,
} from 'lucide-react';

const euro = (n) => `€${Number(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const fmtLongDate = (d) => { try { return new Date(d).toLocaleDateString('it-IT', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }); } catch { return ''; } };

export default function CourseDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [enrollOpen, setEnrollOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true); setNotFound(false);
    coursesService.publicGet(slug)
      .then((d) => { if (alive) { setCourse(d); setLoading(false); } })
      .catch(() => { if (alive) { setNotFound(true); setLoading(false); } });
    return () => { alive = false; };
  }, [slug]);

  if (loading) {
    return (
      <PublicLayout>
        <div className="sila-section py-10">
          <Skeleton className="h-72 w-full rounded-2xl" />
          <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_340px]">
            <div className="space-y-4"><Skeleton className="h-8 w-2/3" /><Skeleton className="h-40 w-full" /></div>
            <Skeleton className="h-72 w-full rounded-2xl" />
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (notFound || !course) {
    return (
      <PublicLayout>
        <div className="sila-section flex flex-col items-center py-24 text-center" data-testid="course-not-found">
          <GraduationCap className="h-12 w-12 text-muted-foreground" />
          <h1 className="mt-4 font-display text-2xl font-bold">Corso non disponibile</h1>
          <p className="mt-2 text-muted-foreground">Il corso che cerchi non esiste o non è più pubblicato.</p>
          <Button className="mt-6" onClick={() => navigate('/educational/corsi-online')}>Vedi i corsi</Button>
        </div>
      </PublicLayout>
    );
  }

  const isOnline = (course.mode || 'online') === 'online';
  const cover = course.cover ? mediaUrl(course.cover) : null;
  const storefront = isOnline ? '/educational/corsi-online' : '/educational/in-sila';
  const gallery = (course.gallery || []).map((g) => mediaUrl(g)).filter(Boolean);

  return (
    <PublicLayout>
      <Seo
        title={course.seo?.meta_title || `${course.title} — Corsi Sila Experiences`}
        description={course.seo?.meta_description || course.short || `Iscriviti al corso ${course.title}.`}
        image={cover}
        path={`/educational/corso/${course.slug}`}
        type="article"
      />

      {/* Hero */}
      <section className="relative isolate flex min-h-[46vh] items-end overflow-hidden text-white" data-testid="course-detail-hero">
        {cover ? (
          <img src={cover} alt={course.title} className="absolute inset-0 -z-10 h-full w-full object-cover" />
        ) : (
          <div aria-hidden className="absolute inset-0 -z-10 bg-gradient-to-br from-primary/80 to-primary" />
        )}
        <div aria-hidden className="absolute inset-0 -z-10" style={{ background: 'linear-gradient(180deg, hsl(150 42% 8% / 0.35) 0%, hsl(150 42% 8% / 0.6) 55%, hsl(150 42% 8% / 0.92) 100%)' }} />
        <div className="sila-section w-full pb-12 pt-28">
          <Link to={storefront} className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-white/85 hover:text-white" data-testid="course-detail-back">
            <ArrowLeft className="h-4 w-4" /> Tutti i corsi
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide backdrop-blur-md">
              {isOnline ? <Monitor className="h-3.5 w-3.5" /> : <MapPin className="h-3.5 w-3.5" />}{isOnline ? 'Online' : 'In presenza'}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide backdrop-blur-md">
              <GraduationCap className="h-3.5 w-3.5" /> Educational
            </span>
          </div>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-bold leading-tight sm:text-5xl" data-testid="course-detail-title">{course.title}</h1>
          {course.short && <p className="mt-3 max-w-2xl text-lg text-white/85">{course.short}</p>}
          {course.owner_name && <p className="mt-2 text-sm text-white/75">con {course.owner_name}</p>}
        </div>
      </section>

      {/* Body */}
      <section className="sila-section py-12">
        <div className="grid gap-8 lg:grid-cols-[1fr_340px]">
          {/* Colonna principale */}
          <div className="min-w-0">
            {course.description ? (
              <div className="sila-prose max-w-none" data-testid="course-detail-description" dangerouslySetInnerHTML={{ __html: course.description }} />
            ) : (
              <p className="text-muted-foreground">Nessuna descrizione disponibile.</p>
            )}

            {/* Sessioni */}
            {(course.sessions || []).length > 0 && (
              <div className="mt-10" data-testid="course-detail-sessions">
                <h2 className="font-display text-xl font-bold">Calendario e sessioni</h2>
                <div className="mt-4 space-y-3">
                  {course.sessions.map((s) => {
                    const soldOut = s.left <= 0;
                    return (
                      <Card key={s.id} className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`course-detail-session-${s.id}`}>
                        <div className="flex items-start gap-3">
                          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                            <CalendarDays className="h-5 w-5" />
                          </div>
                          <div>
                            <p className="font-semibold capitalize text-foreground">{fmtLongDate(s.start)}</p>
                            <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-muted-foreground">
                              {s.time && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{s.time}</span>}
                              {s.duration_min > 0 && <span>{s.duration_min} min</span>}
                              {isOnline ? (
                                <span className="inline-flex items-center gap-1"><Video className="h-3.5 w-3.5" />Online</span>
                              ) : s.location?.name ? (
                                <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{s.location.name}</span>
                              ) : null}
                            </p>
                          </div>
                        </div>
                        {s.left < 999 && (
                          <Badge variant={soldOut ? 'destructive' : 'secondary'}>{soldOut ? 'Esaurito' : `${s.left} posti`}</Badge>
                        )}
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Galleria */}
            {gallery.length > 0 && (
              <div className="mt-10" data-testid="course-detail-gallery">
                <h2 className="font-display text-xl font-bold">Galleria</h2>
                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {gallery.map((g, i) => (
                    <div key={i} className="aspect-[4/3] overflow-hidden rounded-xl">
                      <img src={g} alt={`${course.title} ${i + 1}`} className="h-full w-full object-cover transition-transform duration-300 hover:scale-105" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar iscrizione */}
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <Card className="overflow-hidden" data-testid="course-detail-enroll-card">
              <div className="border-b border-border p-5">
                <p className="text-sm text-muted-foreground">Quota di iscrizione</p>
                <p className="mt-1 font-display text-3xl font-bold text-primary" data-testid="course-detail-price">
                  {Number(course.price) > 0 ? euro(course.price) : 'Gratis'}
                </p>
                {course.sessions_count > 0 && (
                  <p className="mt-1 text-xs text-muted-foreground">{course.sessions_count} session{course.sessions_count === 1 ? 'e' : 'i'} disponibil{course.sessions_count === 1 ? 'e' : 'i'}</p>
                )}
              </div>
              <div className="space-y-3 p-5 text-sm">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-muted-foreground"><Monitor className="h-4 w-4" /> Modalità</span>
                  <span className="font-medium text-foreground">{isOnline ? 'Online' : 'In presenza'}</span>
                </div>
                {course.level && (
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-muted-foreground"><BarChart3 className="h-4 w-4" /> Livello</span>
                    <span className="font-medium text-foreground">{course.level}</span>
                  </div>
                )}
                {course.language && (
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-muted-foreground"><Globe className="h-4 w-4" /> Lingua</span>
                    <span className="font-medium text-foreground">{course.language}</span>
                  </div>
                )}
                {!isOnline && course.location && (
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-muted-foreground"><MapPin className="h-4 w-4" /> Luogo</span>
                    <span className="font-medium text-foreground">{course.location}</span>
                  </div>
                )}
              </div>
              <div className="p-5 pt-0">
                <Button className="h-11 w-full rounded-full text-sm font-semibold" onClick={() => setEnrollOpen(true)} data-testid="course-detail-enroll-button">
                  <GraduationCap className="mr-2 h-4 w-4" /> Iscriviti ora
                </Button>
                <p className="mt-2 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
                  <Users className="h-3.5 w-3.5" /> Anche senza registrazione
                </p>
              </div>
            </Card>
          </aside>
        </div>
      </section>

      <CourseEnrollDialog course={course} open={enrollOpen} onClose={() => setEnrollOpen(false)} />
    </PublicLayout>
  );
}
